import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Typography, Progress, Space, Tooltip, Modal, App } from 'antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  DownOutlined,
  RightOutlined,
  RocketOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { usePrimeirosPassos } from '../../hooks/usePrimeirosPassos';
import { useAuth } from '../../contexts/AuthContext';
import { updatePerfil } from '../../services/userService';
import {
  ativarModoPrestador,
  recusarModoPrestadorPerfil,
} from '../../utils/modoPrestador';
import { consumePrestadorIntent, hasPrestadorIntent } from '../../utils/prestadorIntentStorage';
import { getErrorMessage } from '../../utils/errorHandler';

const { Text } = Typography;

const OBJETIVOS = [
  { value: 'financas', label: 'Minhas finanças', hint: 'Contas, cartões, faturas e o mês' },
  { value: 'milhas', label: 'Minhas milhas', hint: 'Programas, saldos e custo do milheiro' },
  { value: 'ambos', label: 'Os dois', hint: 'Finanças e milhas no mesmo lugar' },
];

function PrimeirosPassosCard() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const {
    user, refreshUser, temModoPrestador, temModuloFinanceiro, temModuloMilhas,
  } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { visible, loading, steps, doneRequired, totalRequired, dismiss, restore } = usePrimeirosPassos();
  const [perfilModalOpen, setPerfilModalOpen] = useState(false);
  const [perfilSaving, setPerfilSaving] = useState(false);
  const [mostrarOpcionais, setMostrarOpcionais] = useState(false);
  const [salvandoObjetivo, setSalvandoObjetivo] = useState(false);

  // Primeiro acesso com os dois módulos: pergunta o que a pessoa quer organizar,
  // para o guia mostrar só o que importa (e o checkout sugerir o plano certo).
  const precisaObjetivo = Boolean(
    temModuloFinanceiro && temModuloMilhas && user && !user.preferencias_ui?.objetivo,
  );

  const escolherObjetivo = async (objetivo) => {
    setSalvandoObjetivo(true);
    try {
      await updatePerfil({ preferencias_ui: { objetivo } });
      await refreshUser();
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível salvar. Tente de novo.'));
    } finally {
      setSalvandoObjetivo(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('primeiros') !== '1') return;
    restore();
    const next = new URLSearchParams(searchParams);
    next.delete('primeiros');
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams, setSearchParams, restore]);

  // Deep link LP: ?perfil=prestador → sugerir ativação após login/cadastro
  useEffect(() => {
    if (loading || !user) return;
    if (temModoPrestador) {
      if (hasPrestadorIntent()) consumePrestadorIntent();
      return;
    }
    const prefs = user.preferencias_ui || {};
    if (prefs.modo_prestador_perfil_respondido) {
      if (hasPrestadorIntent()) consumePrestadorIntent();
      return;
    }
    if (!hasPrestadorIntent()) return;
    consumePrestadorIntent();
    setPerfilModalOpen(true);
  }, [loading, user, temModoPrestador]);

  const handleStepClick = (step) => {
    if (step.action === 'perfil-prestador') {
      setPerfilModalOpen(true);
      return;
    }
    if (step.path) navigate(step.path);
  };

  const handleAtivarPrestador = async () => {
    setPerfilSaving(true);
    try {
      await ativarModoPrestador(updatePerfil);
      await refreshUser();
      message.success('Ferramentas de prestador ativadas.');
      setPerfilModalOpen(false);
      navigate('/a-receber');
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível ativar.'));
    } finally {
      setPerfilSaving(false);
    }
  };

  const handleRecusarPrestador = async () => {
    setPerfilSaving(true);
    try {
      await recusarModoPrestadorPerfil(updatePerfil);
      await refreshUser();
      setPerfilModalOpen(false);
    } catch (err) {
      message.error(getErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setPerfilSaving(false);
    }
  };

  const percent = totalRequired > 0 ? Math.round((doneRequired / totalRequired) * 100) : 0;
  // Essenciais (e o Telegram) sempre à vista; o resto fica recolhido para não assustar no começo.
  const opcionais = steps.filter((st) => !st.required && !st.destaque);
  const stepsVisiveis = mostrarOpcionais ? steps : steps.filter((st) => st.required || st.destaque);
  const showCard = !loading && visible;

  if (loading && !perfilModalOpen) return null;
  if (!showCard && !perfilModalOpen) return null;

  return (
    <>
      {showCard ? (
        <Card
          style={{ marginBottom: 24 }}
          styles={{ body: { padding: '16px 20px' } }}
          title={
            <Space>
              <RocketOutlined />
              <span>Primeiros passos</span>
            </Space>
          }
          extra={
            <Tooltip title="Ocultar este guia (você pode reabrir no menu do perfil)">
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={dismiss} aria-label="Dispensar">
                Já sei usar
              </Button>
            </Tooltip>
          }
        >
          {precisaObjetivo ? (
            <>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                O que você quer organizar primeiro?
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Assim o guia mostra só os passos que fazem sentido para você. Dá para usar tudo do mesmo jeito.
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {OBJETIVOS.map((o) => (
                  <Button
                    key={o.value}
                    onClick={() => escolherObjetivo(o.value)}
                    loading={salvandoObjetivo}
                    style={{ height: 'auto', padding: '10px 14px', flex: '1 1 160px', textAlign: 'left', whiteSpace: 'normal' }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{o.label}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{o.hint}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </>
          ) : (
          <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Complete estes passos para começar a usar o Saldo Ativo com clareza.
          </Text>
          <Progress percent={percent} size="small" format={() => `${doneRequired} de ${totalRequired}`} style={{ marginBottom: 16 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stepsVisiveis.map((step) => {
              const disabled = Boolean(step.blocked && !step.done);
              return (
                <div
                  key={step.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: step.done ? 'rgba(82, 196, 26, 0.06)' : 'rgba(0,0,0,0.02)',
                    opacity: disabled ? 0.65 : 1,
                  }}
                >
                  <CheckCircleOutlined
                    style={{ fontSize: 18, color: step.done ? '#52c41a' : '#bfbfbf', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong delete={step.done} style={{ display: 'block' }}>
                      {step.label}
                      {!step.required ? (
                        <Text type="secondary" style={{ fontWeight: 400 }}>
                          {step.destaque ? ' (recomendado)' : ' (opcional)'}
                        </Text>
                      ) : null}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {disabled ? step.blockedHint : step.description}
                    </Text>
                  </div>
                  {!step.done ? (
                    <Button
                      type="link"
                      size="small"
                      disabled={disabled}
                      onClick={() => handleStepClick(step)}
                      icon={<RightOutlined />}
                    >
                      {step.action === 'perfil-prestador' ? 'Responder' : 'Ir'}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
          {opcionais.length > 0 && (
            <Button
              type="link"
              size="small"
              style={{ marginTop: 8, paddingLeft: 0 }}
              icon={mostrarOpcionais ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setMostrarOpcionais((v) => !v)}
            >
              {mostrarOpcionais
                ? 'Mostrar menos'
                : `Mais recursos para depois (${opcionais.filter((o) => !o.done).length} opcionais)`}
            </Button>
          )}
          </>
          )}
        </Card>
      ) : null}

      <Modal
        title="Você cobra clientes com frequência?"
        open={perfilModalOpen}
        onCancel={() => setPerfilModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Cobrança ocasional de amigos pelo WhatsApp funciona sem isso. Ative só se quiser Clientes e o painel A receber.
        </Text>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button type="primary" block loading={perfilSaving} onClick={handleAtivarPrestador}>
            Sim, cobro com frequência
          </Button>
          <Button block disabled={perfilSaving} onClick={handleRecusarPrestador}>
            Não, só ocasionalmente
          </Button>
        </Space>
      </Modal>
    </>
  );
}

export default PrimeirosPassosCard;
